import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import axiosInstance from "src/apis/axiosInstance";

interface HotelEmailTemplateModalProps {
  show: boolean;
  onHide: () => void;
  bookingId: string;
  bookingHotelId: string;
  hotelName?: string;
}

type Channel = "email" | "whatsapp";

interface EmailTemplateData {
  subject: string;
  html: string;
  unresolved_placeholders: string[];
}

function highlightPlaceholders(html: string): string {
  return html.replace(
    /\{\{([^}]+)\}\}/g,
    '<mark style="background:#fff3cd;color:#856404;border-radius:3px;padding:0 2px;font-weight:600;">{{$1}}</mark>'
  );
}

const HotelEmailTemplateModal = ({
  show,
  onHide,
  bookingId,
  bookingHotelId,
  hotelName,
}: HotelEmailTemplateModalProps) => {
  const [channel, setChannel] = useState<Channel>("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [unresolvedPlaceholders, setUnresolvedPlaceholders] = useState<string[]>([]);
  const [rawHtml, setRawHtml] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Reset all state when modal closes
  useEffect(() => {
    if (!show) {
      setChannel("email");
      setLoading(false);
      setError(null);
      setSubject("");
      setUnresolvedPlaceholders([]);
      setRawHtml(null);
    }
  }, [show]);

  // Fetch template when modal is open — runs for both channels
  useEffect(() => {
    if (!show) return;

    setLoading(true);
    setError(null);
    setSubject("");
    setUnresolvedPlaceholders([]);
    setRawHtml(null);

    axiosInstance
      .post("BookingApi/booking-hotel-email-template/", {
        booking_id: bookingId,
        booking_hotel_id: bookingHotelId,
        type: channel === "email" ? "Email" : "whatsapp",
      })
      .then((res) => {
        const data: EmailTemplateData = res.data?.data;
        setUnresolvedPlaceholders(data?.unresolved_placeholders || []);
        setSubject(data?.subject || "");
        setRawHtml(data?.html || "");
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.data ||
          err?.response?.data?.message ||
          "Failed to load template.";
        setError(typeof msg === "string" ? msg : "Failed to load template.");
      })
      .finally(() => setLoading(false));
  }, [show, channel, bookingId, bookingHotelId]);

  // Set editor HTML after it renders (rawHtml changes after loading becomes false)
  useEffect(() => {
    if (rawHtml !== null && editorRef.current) {
      editorRef.current.innerHTML = highlightPlaceholders(rawHtml);
    }
  }, [rawHtml]);

  const handleEditorInput = () => {
    const text = editorRef.current?.innerText || "";
    setUnresolvedPlaceholders((prev) =>
      prev.filter((p) => text.includes(`{{${p}}}`))
    );
  };

  // Strips the <mark> highlight wrappers added for placeholder visibility,
  // returning clean HTML suitable for copying.
  const getCleanHtml = (): string => {
    if (!editorRef.current) return "";
    const clone = editorRef.current.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("mark").forEach((mark) => {
      mark.replaceWith(document.createTextNode(mark.textContent || ""));
    });
    return clone.innerHTML;
  };

  // Shared rich-HTML copy logic used by both channels.
  const copyRichHtml = async (successMsg: string) => {
    const cleanHtml = getCleanHtml();
    if (!cleanHtml) {
      toast.error("Nothing to copy");
      return;
    }
    // Modern Clipboard API with rich HTML (requires HTTPS or localhost)
    if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
      try {
        const blob = new Blob([cleanHtml], { type: "text/html" });
        await navigator.clipboard.write([new ClipboardItem({ "text/html": blob })]);
        toast.success(successMsg);
        return;
      } catch (err) {
        console.warn("ClipboardItem write failed, trying writeText:", err);
      }
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(cleanHtml);
        toast.warning("Copied as HTML text — formatting may not be preserved");
        return;
      } catch (err) {
        console.warn("clipboard.writeText failed, falling back to execCommand:", err);
      }
    }
    // Fallback: works on HTTP — selecting contentEditable preserves formatting
    try {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current!);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      const ok = document.execCommand("copy");
      sel?.removeAllRanges();
      if (ok) {
        toast.success(successMsg);
      } else {
        toast.error("Copy failed — please copy manually");
      }
    } catch (err) {
      console.error("execCommand copy failed:", err);
      toast.error("Copy failed — please copy manually");
    }
  };

  const handleCopyForEmail = () =>
    copyRichHtml("Template copied — paste directly into your email");

  const handleCopyForWhatsApp = async () => {
    const text = editorRef.current?.innerText || "";
    if (!text.trim()) {
      toast.error("Nothing to copy");
      return;
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Template copied — paste into WhatsApp");
        return;
      } catch (err) {
        console.warn("clipboard.writeText failed, falling back to execCommand:", err);
      }
    }
    // Fallback: works on HTTP
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success("Template copied — paste into WhatsApp");
      } else {
        toast.error("Copy failed — please copy manually");
      }
    } catch (err) {
      console.error("execCommand copy failed:", err);
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {hotelName ? `Booking Template — ${hotelName}` : "Hotel Booking Template"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Channel selector */}
        <div className="mb-3" style={{ maxWidth: 240 }}>
          <label className="form-label fw-semibold mb-1">Channel</label>
          <select
            className="form-select"
            value={channel}
            onChange={(e) => setChannel(e.target.value as Channel)}
          >
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-5">
            <div
              className={`spinner-border ${channel === "email" ? "text-primary" : "text-success"}`}
              role="status"
            />
            <div className="mt-2 text-muted small">Loading template…</div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {/* Content — shared for both channels */}
        {!loading && !error && rawHtml !== null && (
          <>
            {/* Subject — email only */}
            {channel === "email" && (
              <div className="mb-3">
                <label className="form-label fw-semibold mb-1">Subject</label>
                <input
                  type="text"
                  className="form-control"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            )}

            {/* Unresolved placeholders warning */}
            {unresolvedPlaceholders.length > 0 && (
              <div className="alert alert-warning mb-3">
                <div className="fw-semibold mb-1">
                  <i className="fas fa-exclamation-triangle me-1" />
                  Unresolved Placeholders
                </div>
                <div className="small mb-2">
                  The following fields could not be filled automatically.
                  They are highlighted in the template below — edit them before sending.
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {unresolvedPlaceholders.map((p) => (
                    <span
                      key={p}
                      style={{
                        background: "#fff3cd",
                        color: "#856404",
                        border: "1px solid #ffe69c",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: 12,
                        fontFamily: "monospace",
                        fontWeight: 600,
                      }}
                    >
                      {`{{${p}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Label + copy button */}
            <div className="mb-2 d-flex justify-content-between align-items-center">
              <label className="form-label fw-semibold mb-0">
                {channel === "email" ? "Email Body" : "WhatsApp Message"}
              </label>
              {channel === "email" ? (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleCopyForEmail}
                  title="Copy formatted template — paste directly into Gmail, Outlook, etc."
                >
                  <i className="fas fa-envelope me-1" />
                  Copy for Email
                </button>
              ) : (
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleCopyForWhatsApp}
                  title="Copy formatted template — paste into WhatsApp"
                >
                  <i className="fab fa-whatsapp me-1" />
                  Copy for WhatsApp
                </button>
              )}
            </div>

            {/* Editable HTML preview — shared editor for both channels */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleEditorInput}
              style={{
                border: "1px solid #d9e2ec",
                borderRadius: 4,
                minHeight: 400,
                maxHeight: "60vh",
                overflowY: "auto",
                padding: 0,
                outline: "none",
                background: "#fff",
              }}
            />
            <div className="text-muted small mt-1">
              Edit the template directly above, then use{" "}
              <strong>{channel === "email" ? "Copy for Email" : "Copy for WhatsApp"}</strong>{" "}
              to paste it into{" "}
              {channel === "email"
                ? "Gmail, Outlook, or any email client with full formatting."
                : "your WhatsApp chat."}
              {" "}Highlighted text indicates unresolved placeholders.
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default HotelEmailTemplateModal;
